/**
 * Rules Catalog Store (Reglas tab)
 */

import { useCallback } from 'react';
import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { Policy } from '@commandDrivenStateEngine/core/policies';
import type { CommandMetaInput, Dispatch } from '@commandDrivenStateEngine/core/types';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import useAuth from '@contexts/useAuth/useAuth';
import { initialState, RulesCatalogState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { RulesCatalogCommand } from './commands';
import {
  createScopeReferenceDataPolicy,
  createRuleTogglePolicy,
  createRuleDeletePolicy
} from './policies';

export { selectors } from './invariants';

const store = createStore({
  name: 'automation-rules-catalog',
  initialState,
  handlers,
  invariants
});

export const {
  useSelector: useRulesCatalogSelector,
  useErrors: useRulesCatalogErrors,
  usePolicies: useRulesCatalogPolicies,
  getState: getRulesCatalogState,
  dispatch: dispatchRulesCatalogCommand
} = store;

/**
 * Dispatch hook that attaches the authenticated user to user-sourced commands
 * so the `canApply` capability gates can authorize them. The engine core stays
 * auth-agnostic; this web-app boundary supplies the actor. An explicit
 * `meta.user` (e.g. from tests) is left untouched.
 */
export const useRulesCatalogDispatch = (): Dispatch<RulesCatalogCommand> => {
  const auth = useAuth();
  const user = auth?.user ?? null;
  return useCallback<Dispatch<RulesCatalogCommand>>(
    ( command, metaInput: CommandMetaInput ) =>
      store.dispatch(
        command,
        metaInput.source === 'user' && metaInput.user === undefined
          ? { ...metaInput, user }
          : metaInput
      ),
    [user]
  );
};

/**
 * Policy factory - creates policies for the rules-catalog store.
 *
 * Wired policies:
 *   - #8: scopeReferenceDataPolicy — fetch rules (incl. bypass) + connected
 *     channels, build the sparse scope tree, dispatch into the catalog.
 *   - #9: ruleTogglePolicy — enable/disable a rule via `SetRule` (full body,
 *     `enabled` flipped) with read-after-write refetch.
 */
export const createRulesCatalogPolicies = (
  backend: ClientLibraryType
): Policy<RulesCatalogState, RulesCatalogCommand>[] => [
  createScopeReferenceDataPolicy( backend ),
  createRuleTogglePolicy( backend ),
  createRuleDeletePolicy( backend )
];
